'use client'

import { useMemo, useState } from 'react'
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronUp,
  IconLock
} from '@tabler/icons-react'
import { Badge } from '@/components/ui/badge'
import { MONETIZATION_ENABLED } from '@/monetization-switch'
import { Button } from '@/components/ui/button'
import { Link } from '@/i18n/routing'
import type { StoryMetadata, StorySeries } from '@/content/stories'
import Image from 'next/image'
import { useLearningStates } from '@/hooks/useLearningState'
import {
  learningContentKey,
  storyLearningLocator
} from '@/lib/learning-state-core'
import {
  buildStorySeriesSummaries,
  type StorySeriesChapterSummary,
  type StorySeriesSummary
} from '@/lib/story-series-progress-core'
import { cn } from '@/lib/utils'

function progressLabel(summary: StorySeriesSummary) {
  if (summary.readCount > 0) {
    return `${summary.readCount}/${summary.chapters.length} read`
  }
  if (summary.startedCount > 0) return 'In progress'
  return `${summary.chapters.length} chapters`
}

function actionCopy(summary: StorySeriesSummary) {
  if (summary.currentChapter) return 'Continue'
  if (summary.readCount > 0) return 'Next'
  return 'Start'
}

function chapterBadge(chapter: StorySeriesChapterSummary) {
  if (chapter.progressStatus === 'read') {
    return <Badge variant="success">Read</Badge>
  }
  if (chapter.progressStatus === 'started') {
    return <Badge variant="outline">Started</Badge>
  }
  if (chapter.isNextChapter) {
    return <Badge variant="secondary">Next chapter</Badge>
  }
  return null
}

function ChapterRow({ chapter }: { chapter: StorySeriesChapterSummary }) {
  const label = chapter.chapterNumber
    ? `Chapter ${chapter.chapterNumber}`
    : 'Story'

  return (
    <Link
      href={`/read/${chapter.slug}`}
      className="hover:bg-muted/45 focus-visible:ring-ring/40 flex min-w-0 items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm transition focus-visible:ring-2 focus-visible:outline-none"
    >
      <span className="min-w-0">
        <span className="text-muted-foreground block text-xs">{label}</span>
        <span className="block truncate font-medium">{chapter.title_en}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        {chapterBadge(chapter)}
        {MONETIZATION_ENABLED && chapter.isPremiumChapter && (
          <Badge
            variant="outline"
            className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          >
            <IconLock className="h-3 w-3" />
            Pro
          </Badge>
        )}
        <IconArrowRight className="text-muted-foreground h-4 w-4" />
      </span>
    </Link>
  )
}

function SeriesCard({
  summary,
  expanded,
  onToggle
}: {
  summary: StorySeriesSummary
  expanded: boolean
  onToggle: () => void
}) {
  const primaryHref = summary.primaryChapter
    ? `/read/${summary.primaryChapter.slug}`
    : '/read'
  const coverImage =
    summary.primaryChapter?.coverImage ?? summary.chapters[0]?.coverImage
  const progress = progressLabel(summary)

  return (
    <article
      className={cn(
        'group bg-card hover:border-foreground/25 hover:bg-muted/20 flex h-full min-w-0 snap-start flex-col overflow-hidden rounded-lg border transition',
        expanded && 'border-foreground/25 bg-muted/35'
      )}
    >
      {coverImage && (
        <Link
          href={primaryHref}
          className="bg-muted relative block aspect-[16/9] overflow-hidden outline-none"
          aria-label={`${actionCopy(summary)} series: ${summary.series.title}`}
        >
          <Image
            src={coverImage.src}
            alt={coverImage.alt}
            fill
            sizes="(min-width: 1280px) 280px, (min-width: 768px) 50vw, 82vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.015]"
          />
          <span
            className="bg-background/82 text-foreground/85 ring-border/45 absolute top-2 right-2 inline-flex h-6 max-w-[70%] items-center rounded-full px-2 text-[10px] font-semibold shadow-sm ring-1 backdrop-blur-sm"
            aria-label={progress}
            title={progress}
          >
            {progress}
          </span>
        </Link>
      )}
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="text-muted-foreground block text-xs font-semibold tracking-wide uppercase">
              Series
            </span>
            <span className="mt-1 block truncate text-base font-semibold">
              {summary.series.title}
            </span>
          </span>
          {!coverImage && (
            <span
              className="bg-muted/60 text-muted-foreground inline-flex h-6 shrink-0 items-center rounded-full px-2 text-[10px] font-semibold"
              aria-label={progress}
              title={progress}
            >
              {progress}
            </span>
          )}
        </div>
        <div className="min-w-0">
          {summary.series.description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-5">
              {summary.series.description}
            </p>
          )}
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-end gap-2 pt-4">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2.5"
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {expanded ? 'Hide' : 'Chapters'}
              {expanded ? (
                <IconChevronUp className="h-4 w-4" />
              ) : (
                <IconChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button asChild size="sm" className="h-8 px-2.5">
              <Link href={primaryHref}>
                {actionCopy(summary)}
                <IconArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        {expanded && (
          <div className="border-border/70 mt-4 border-t pt-2">
            <div className="divide-border divide-y">
              {summary.chapters.map((chapter) => (
                <ChapterRow key={chapter.slug} chapter={chapter} />
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

export function SeriesShelf({
  seriesRegistry,
  stories
}: {
  seriesRegistry: StorySeries[]
  stories: StoryMetadata[]
}) {
  const chapterSlugs = useMemo(
    () =>
      Array.from(
        new Set(seriesRegistry.flatMap((series) => series.chapterSlugs))
      ),
    [seriesRegistry]
  )
  const chapterLocators = useMemo(
    () => chapterSlugs.map((slug) => storyLearningLocator(slug)),
    [chapterSlugs]
  )
  const { data: chapterStates = new Map() } = useLearningStates(chapterLocators)
  const progressBySlug = useMemo(
    () =>
      new Map(
        chapterSlugs.map((slug) => {
          const state = chapterStates.get(
            learningContentKey(storyLearningLocator(slug))
          )
          return [
            slug,
            {
              status: state?.status ?? null,
              progress: state?.progress ?? null
            }
          ] as const
        })
      ),
    [chapterSlugs, chapterStates]
  )
  const summaries = useMemo(
    () =>
      buildStorySeriesSummaries({
        seriesRegistry,
        stories,
        progressBySlug
      }),
    [progressBySlug, seriesRegistry, stories]
  )
  const [expandedSeriesId, setExpandedSeriesId] = useState<string | null>(null)

  if (!summaries.length) return null

  return (
    <section className="min-w-0 space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Series</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Follow short story arcs without leaving the story library.
        </p>
      </div>
      <div
        role="region"
        aria-label="Story series"
        tabIndex={0}
        className="-mx-4 snap-x overflow-x-auto px-4 pb-3 md:mx-0 md:px-0"
      >
        <div className="grid auto-cols-[minmax(15rem,82vw)] grid-flow-col items-stretch gap-3 sm:auto-cols-[minmax(17rem,44vw)] lg:auto-cols-[minmax(17rem,24rem)]">
          {summaries.map((summary) => (
            <SeriesCard
              key={summary.series.id}
              summary={summary}
              expanded={summary.series.id === expandedSeriesId}
              onToggle={() =>
                setExpandedSeriesId((current) =>
                  current === summary.series.id ? null : summary.series.id
                )
              }
            />
          ))}
        </div>
      </div>
    </section>
  )
}
